import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { ShieldCheck, Lock, Zap, ChevronDown, Undo2, UserRoundCog, TriangleAlert, Inbox, UserRoundPlus } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import QCReturnModal from './QCReturnModal';
import QcOverrideCompleteButton, { canOverrideQc } from './QcOverrideCompleteButton';
import { postQcReturn } from '../../lib/qcComponent';
import './qc-review-banner.css';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/**
 * QC-REVIEW-UI-002 — compact single-row "Order Under QC Review" banner.
 * UI only: every action reuses the existing handler / endpoint / confirmation:
 *   Send Back to Processor  → QCReturnModal → POST qc-return (postQcReturn, shared with the QC flow)
 *   Reassign QC Reviewer    → existing POST qc-unassign (release to queue) / POST qc-assign (assign to me)
 *   Emergency Override      → the one QcOverrideCompleteButton dialog (mandatory reason, admin only)
 * The QC Actions menu is admin-only (matches the previous admin-only guidance banner).
 */
export default function QcReviewLockBanner({ orderData, user, onRefreshOrder, overrideWorkflow, onOverrideCompleted }) {
  const [showReturn, setShowReturn] = useState(false);
  const [showReassign, setShowReassign] = useState(false);
  const [busy, setBusy] = useState(false);
  const overrideRef = useRef(null);
  const isAdmin = user?.role === 'admin';
  const canOverride = canOverrideQc(user?.role);
  const reviewer = orderData?.qc_assigned_to_name;
  const assignedToMe = orderData?.qc_assigned_to && orderData.qc_assigned_to === user?.id;

  const returnConfirm = async (category, errorType, notes, evidenceFileIds, severity, affectedComponents = null) => {
    if (!category || !notes) return;
    try {
      const res = await postQcReturn(orderData, { category, errorType, notes, evidenceFileIds, severity, affectedComponents });
      if (res.ok) {
        toast.success(`${orderData.order_number} sent back to the processor for correction.`);
        setShowReturn(false);
        onRefreshOrder?.();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Send back failed.');
      }
    } catch { toast.error('Network error.'); }
  };

  const qcAssignment = async (action) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/orders/${orderData.id}/${action}`, { method: 'POST', headers: authHeaders() });
      if (res.ok) {
        toast.success(action === 'qc-unassign' ? `${orderData.order_number} released to the QC queue.` : `${orderData.order_number} assigned to you for QC.`);
        setShowReassign(false);
        onRefreshOrder?.();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'QC assignment change failed.');
      }
    } catch { toast.error('Network error.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="qcr-banner" data-testid={isAdmin ? 'qc-review-admin-banner' : 'qc-review-lock-banner'}>
      <div className="qcr-icon" aria-hidden="true"><ShieldCheck className="w-6 h-6" strokeWidth={1.8} /></div>
      <div className="qcr-divider" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2.5 flex-wrap">
          <h3 className="text-lg font-bold text-slate-900 leading-tight" data-testid="qc-review-banner-title">Order Under QC Review</h3>
          <span className="qcr-badge" data-testid="qc-review-readonly-badge"><Lock className="w-3 h-3" /> Read-only</span>
          {reviewer && <span className="qcr-reviewer" data-testid="qc-review-reviewer">Reviewer: {reviewer}</span>}
        </div>
        <p className="text-sm text-slate-600 mt-0.5 sm:truncate" data-testid="qc-review-banner-text">This order is locked during QC review to maintain audit integrity.</p>
      </div>

      {isAdmin && (
        <div className="qcr-actions">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="qcr-menu-btn" data-testid="qc-actions-trigger">
                <Zap className="w-4 h-4" /> QC Actions <ChevronDown className="w-4 h-4 qcr-chevron" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={8} className="qcr-menu" data-testid="qc-actions-menu">
              <DropdownMenuItem className="qcr-item" onSelect={() => setShowReturn(true)} data-testid="qc-action-send-back">
                <Undo2 className="w-4 h-4 text-blue-600" /> Send Back to Processor
              </DropdownMenuItem>
              <DropdownMenuItem className="qcr-item" onSelect={() => setShowReassign(true)} data-testid="qc-action-reassign">
                <UserRoundCog className="w-4 h-4 text-indigo-600" /> Reassign QC Reviewer
              </DropdownMenuItem>
              {canOverride && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="qcr-item qcr-item-danger" onSelect={() => overrideRef.current?.open()} data-testid="qc-action-override">
                    <TriangleAlert className="w-4 h-4" /> Emergency Override (Exceptional)
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <QcOverrideCompleteButton
            ref={overrideRef}
            variant="card"
            orderId={orderData?.id}
            orderNumber={orderData?.order_number}
            workflow={overrideWorkflow}
            userRole={user?.role}
            orderStatus={orderData?.status}
            qcStatus={orderData?.qc_status}
            qcIteration={orderData?.qc_iteration}
            onCompleted={onOverrideCompleted}
          />
        </div>
      )}

      {isAdmin && (
        <>
          {showReturn && createPortal(
            <QCReturnModal isOpen={showReturn} onClose={() => setShowReturn(false)} onConfirm={returnConfirm} order={orderData} />,
            document.body)}
          <AlertDialog open={showReassign} onOpenChange={(o) => { if (!busy) setShowReassign(o); }}>
            <AlertDialogContent className="max-w-md" data-testid="qc-reassign-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2"><UserRoundCog className="w-5 h-5 text-indigo-600" /> Reassign QC Reviewer</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="text-sm text-slate-600 space-y-2 text-left">
                    <p>Current reviewer: <strong className="text-slate-900" data-testid="qc-reassign-current">{reviewer || 'Unassigned (in QC queue)'}</strong>.</p>
                    <p>Release the order back to the QC queue so another QC Specialist can claim it, or take it on yourself. The order stays in QC Review and remains read-only.</p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="sm:justify-between gap-2">
                <AlertDialogCancel disabled={busy} data-testid="qc-reassign-cancel">Cancel</AlertDialogCancel>
                <div className="flex gap-2">
                  {orderData?.qc_assigned_to && (
                    <AlertDialogAction className="bg-white text-slate-800 border border-slate-300 hover:bg-slate-50" disabled={busy}
                      onClick={(e) => { e.preventDefault(); qcAssignment('qc-unassign'); }} data-testid="qc-reassign-release">
                      <Inbox className="w-4 h-4 mr-1.5" /> Release to QC Queue
                    </AlertDialogAction>
                  )}
                  {!assignedToMe && (
                    <AlertDialogAction className="bg-indigo-600 hover:bg-indigo-700" disabled={busy}
                      onClick={(e) => { e.preventDefault(); qcAssignment('qc-assign'); }} data-testid="qc-reassign-assign-me">
                      <UserRoundPlus className="w-4 h-4 mr-1.5" /> Assign to Me
                    </AlertDialogAction>
                  )}
                </div>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}
